import { useCallback, useMemo, useState } from 'react';

/**
 *
 * Ephemeral, non-persisted column-visibility layer that temporarily overrides
 * the persisted `columnsVisibility` from `useTableParams`. Intended for
 * mode-specific UIs (e.g., “Prioritize” mode) so you can show a reduced set
 * of columns without losing the user's base preferences.
 *
 * Behavior
 * - If an override map is set, it becomes the single source of truth.
 * - If cleared, the hook falls back to the persisted base visibility.
 *
 * @param baseVisibility - Persisted visibility map from useTableParams
 *
 * @returns {
 *   effectiveVisibility: Record<string, boolean>; // use this to compute visibleColumns
 *   setColumnsVisibilityOverride(map): void;      // apply an ephemeral map
 *   clearColumnsVisibilityOverride(): void;       // remove the override
 * }
 *
 */

type VisibilityMap = Record<string, boolean>;

export const useColumnsVisibilityOverride = ({
	baseVisibility,
}: {
	baseVisibility: VisibilityMap;
}) => {
	const [override, setOverride] = useState<VisibilityMap | null>(null);

	const setColumnsVisibilityOverride = useCallback((map: VisibilityMap) => setOverride(map), []);
	const clearColumnsVisibilityOverride = useCallback(() => setOverride(null), []);

	const effectiveVisibility = useMemo(() => override ?? baseVisibility, [override, baseVisibility]);

	return {
		effectiveVisibility,
		setColumnsVisibilityOverride,
		clearColumnsVisibilityOverride,
	};
};
